import { useTradex } from "@/hooks/use-tradex/index";
import { pdfBasedBL } from "@/pages/record-transfer/etr-issuance/record-transfer-share";
import addFormats from "@/utils/custom-ajv-formats";
import { feature, features as featureConstant } from '@/utils/feature-verifier';
import Ajv from "ajv";
import { produce } from "immer";
import { cloneDeep } from 'lodash';
import { useState } from "react";
import { useHealthdex } from "../use-healthdex/index";

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const useEForm = (readOnly = false) => {
	const [payload, setPayload] = useState(null);
	const [isDirty, setIsDirty] = useState(false);
	const [isReadOnly, setIsReadOnly] = useState(readOnly);
	const isETRIGPNIFeatureEnabled = feature(featureConstant?.ETR_IGPNI);

	const isTradex = useTradex();
	const isHealthDex = useHealthdex();

	function getSchemaWithoutAttachment(schema, dataElementId = null) {
		return produce(schema, (draft) => {
			// remove attachment from schema
			delete draft?.properties?.attachments;
			if (draft?.properties && Object.keys(draft?.properties).length === 0) {
				draft.additionalProperties = true;
			}
			if (Array.isArray(draft?.required)) {
				draft.required = draft.required.filter(
					(item) => item !== "attachments"
				);
			}
			if ((!isTradex) || (isETRIGPNIFeatureEnabled && pdfBasedBL.includes(dataElementId))) {
				if (draft?.properties) {
					for (const [key, value] of Object.entries(draft?.properties)) {
						draft.properties[key] = getSchemaWithoutAttachment(draft?.properties[key], dataElementId);
					}
				}
			}
		});
	}

	function extractCombinedSchema(schema, payload) {
		let keyword;
		// currently only support anyOf and oneOf
		if (Array.isArray(schema?.oneOf)) {
			keyword = "oneOf";
		} else if (Array.isArray(schema?.anyOf)) {
			keyword = "anyOf";
		}

		if (!keyword) {
			return schema;
		}

		if (!payload) {
			return schema[keyword][0];
		}

		return schema[keyword].find((subSchema) => {
			const validate = ajv.compile(subSchema);
			return validate(payload);
		});
	}

	function oldGetFullJson(schema, payload, schemaWithAttachments) {
		let data = (payload !== null && payload !== undefined) ? cloneDeep(payload) : null;

		const type = Array.isArray(schema?.type)
			? schema?.type.find((d) => d !== "null")
			: schema?.type;

		switch (type) {
			case "string":
				if (data !== undefined) {
					return data;
				} else if (schema.format?.includes('date')) {
					return null;
				}
				return "";
			case "number":
			case "integer":
				return data ?? null;
			case "boolean":
				return isHealthDex ? data : (data || false);
			case "radio":
				return isHealthDex ? data : (data || false);
			case "array":
				return data
					? data.map((item) => getFullJson(schema.items, item, schemaWithAttachments.items))
					: [getFullJson(schema.items, undefined, schemaWithAttachments.items)]?.filter((item) => item !== undefined && item !== null);
			case "object":
				if (
					schemaWithAttachments?.properties?.attachments &&
					Array.isArray(schemaWithAttachments?.required) &&
					schemaWithAttachments?.required?.includes("attachments")
				) {
					return null;
				}
				if (!data) {
					data = {};
				}
				for (let prop in schema.properties) {
					data[prop] = getFullJson(
						schema.properties[prop],
						data[prop],
						schemaWithAttachments.properties[prop],
					);
				}
				return data;
			default:
				return data;
		}
	}

	function updatedGetFullJson(schema, payload) {
		let data = (payload !== null && payload !== undefined) ? cloneDeep(payload) : null;

		const type = Array.isArray(schema?.type) && !schema?.type.includes('array') && !schema?.type.includes('object')
			? schema?.type.find(t => t !== "null")
			: schema?.type;

		switch (type) {
			case "string":
				if (data !== undefined) {
					return data;
				} else if (schema.format?.includes('date')) {
					return null;
				}
				return "";
			case "number":
			case "integer":
				return data ?? null;
			case "boolean":
				return isHealthDex ? data : (data || false);
			case "radio":
				return isHealthDex ? data : (data || false);
			case "array":
				return data
					? data.map((item) => getFullJson(schema.items, item))
					: [getFullJson(schema.items)]?.filter((item) => item !== undefined && item !== null);
			case "object":
				if (isETRIGPNIFeatureEnabled && schema?.['x-uitype'] === 'file-upload') return null;
				if (!data) {
					data = {};
				}
				for (let prop in schema.properties) {
					data[prop] = getFullJson(
						schema.properties[prop],
						data[prop]
					);
				}
				if (Object.keys(data).length === 0) {
					data = {};
					for (let prop in schema.properties) {
						data[prop] = getFullJson(schema.properties[prop]);
					}
				}
				return data;
			default:
				if (Array.isArray(schema?.type)) {
					if (schema?.type.includes('array')) {
						if (data && Array.isArray(data)) {
							const processedData = data.map(item => getFullJson(schema.items, item)).filter(item => item !== null && item !== undefined);
							const hasValidData = processedData.length > 0 && processedData.some(item => item !== false || typeof item === 'boolean');
							return hasValidData ? processedData : null;
						}
						return null;
					} else if (schema?.type.includes('object') && schema?.['x-uitype'] !== 'file-upload') {
						if (data && typeof data === 'object' && !Array.isArray(data)) {
							let hasValidData = false;
							for (let prop in schema.properties) {
								const result = getFullJson(schema.properties[prop], data[prop]);
								if (result !== null && result !== undefined) {
									data[prop] = result;
									if (result !== false || typeof result === 'boolean') hasValidData = true;
								} else {
									delete data[prop];
								}
							}
							return hasValidData ? data : null;
						}
						return null;
					}
				} else {
					return data;
				}
		}
	}

	function getFullJson(schema, payload, schemaWithAttachments) {
			return updatedGetFullJson(schema, payload);
	}


	function getFieldValue(payload, parentProps) {
		let temp = payload;
		for (const key of parentProps) {
			if (temp === undefined || temp === null) {
				return undefined;
			}
			temp = temp[key];
		}
		return temp;
	}


	function getNextPayload(payload, parentProps, value) {
		const nextState = produce(payload, (draft) => {
			let temp = draft;

			for (let i = 0; i < parentProps.length; i++) {
				const key = parentProps[i];
				if (i === parentProps.length - 1) {
					temp[key] = value;
				} else {
					temp = temp[key];
				}
			}
		});
		return nextState;
	}

	function sortSchemaFields(schema, strategy = 'DEFAULT') {
		if (typeof schema !== 'object' || !schema?.properties) {
			return schema;
		}

		/** Add sorting weight groups for custom sorting logic here */
		const sortingWeights = {
			NESTED: {
				string: { required: 1, optional: 2 },
				number: { required: 3, optional: 4 },
				integer: { required: 5, optional: 6 },
				boolean: { required: 7, optional: 8 },
				arrayString: { required: 9, optional: 10 },
				arrayNumber: { required: 11, optional: 12 },
				object: { required: 13, optional: 14 },
				array: { required: 15, optional: 16 },
				default: { required: 17, optional: 18 }
			}
		};

		/** To add new strategy: define in strategyHandlers with its sorting logic. */
		const strategyHandlers = {
			DEFAULT: () => 0,
			NESTED: (field, isRequired) => {
				const type = Array.isArray(field.type) ? field.type.find(d => d !== 'null') : field.type;
				const { items } = field;
				const weightKey = isRequired ? 'required' : 'optional';

				const weightTypeMap = {
					string: 'string',
					number: 'number',
					integer: 'integer',
					boolean: 'boolean',
					object: 'object',
					array: {
						string: 'arrayString',
						number: 'arrayNumber',
						default: 'array'
					}
				};

				let weightType = 'default';

				if (type === 'array' && items) {
					weightType = weightTypeMap[type][items.type] || weightTypeMap[type].default;
				} else {
					weightType = weightTypeMap[type] || weightType;
				}

				return sortingWeights.NESTED[weightType][weightKey];
			}
		};

		const getSortWeight = (field, isRequired) => {
			const strategyFunction = strategyHandlers[strategy];
			if (!strategyFunction) {
				console.warn(`Strategy "${strategy}" not found. Defaulting to alphabetical sorting.`);
				return strategyHandlers['DEFAULT']();
			}
			return strategyFunction(field, isRequired);
		};

		const isRequiredField = (field, isRequired) => {
			return isRequired || !(Array.isArray(field.type) && field.type.includes('null'));
		};

		const sortedSchema = JSON.parse(JSON.stringify(schema));

		sortedSchema.properties = Object.keys(sortedSchema.properties)
			.map(key => ({
				key,
				field: sortedSchema.properties[key],
				isRequired: isRequiredField(sortedSchema.properties[key], sortedSchema.required?.includes(key))
			}))
			.sort((a, b) => {
				const weightA = getSortWeight(a.field, a.isRequired);
				const weightB = getSortWeight(b.field, b.isRequired);
				return weightA - weightB || a.key.localeCompare(b.key);
			})
			.reduce((acc, { key, field }) => {
				acc[key] = field;
				return acc;
			}, {});

		Object.keys(sortedSchema.properties).forEach(key => {
			const field = sortedSchema.properties[key];
			if (field.type === 'object' && field.properties) {
				field.properties = sortSchemaFields(field, strategy).properties;
			} else if (field.type === 'array' && field.items?.properties) {
				field.items.properties = sortSchemaFields(field.items, strategy).properties;
			}
		});

		return sortedSchema;
	}

	function checkIfNested(data) {
		if (typeof data !== 'object' || data === null) {
			return false;
		}

		const isNestedType = (type) => {
			if (typeof type === 'string') {
				return type === 'object' || type === 'array';
			} else if (Array.isArray(type)) {
				const nonNullTypes = type.filter(t => t !== 'null');
				return nonNullTypes.some(t => t === 'object' || t === 'array');
			}
			return false;
		};

		return Object.values(data).some(property => {
			return isNestedType(property.type);
		});
	}

	return {
		isDirty,
		payload,
		isReadOnly,
		setIsDirty,
		setPayload,
		setIsReadOnly,
		getFullJson,
		getFieldValue,
		getNextPayload,
		extractCombinedSchema,
		getSchemaWithoutAttachment,
		sortSchemaFields,
		checkIfNested
	};
};

export default useEForm;
